import { noop } from "@tanstack/react-query"
import { createFileRoute, stripSearchParams } from "@tanstack/react-router"
import { z } from "zod"
import { characterLastMessageQueryOptions } from "@/entities/characters"
import { easyMatchStatusQueryOptions } from "@/entities/easy-match"
import { friendsQueryOptions } from "@/entities/friend"
import { subscriptionStatusQueryOptions } from "@/entities/subscription"
import { thoughtsQueryOptions } from "@/entities/thoughts"
import { userQueryOptions } from "@/entities/user"
import { userCardsQueryOptions } from "@/entities/user-card"
import { HomePage } from "@/pages/home"
import { preloadAvatars } from "@/shared/ui/avatar"

// /home without a tab is the "all" tab. The former "new" tab is now the
// /home/new screen; old ?tab=new links fall back to "all" through catch.
const searchDefaults = { tab: "all" } as const

const searchSchema = z.object({
	tab: z
		.enum(["all", "online", "archive"])
		.default(searchDefaults.tab)
		.catch(searchDefaults.tab),
})

// Аватары из дискового кэша успевают к первому кадру; новые с сети не задерживают экран.
const AVATAR_PRELOAD_MS = 300

export const Route = createFileRoute("/_authenticated/home/")({
	validateSearch: searchSchema,
	search: { middlewares: [stripSearchParams(searchDefaults)] },
	codeSplitGroupings: [["loader", "component"]],
	loader: async ({ context }) => {
		const { userId } = context
		const user = await context.queryClient.query({
			...userQueryOptions(userId),
			staleTime: "static",
		})

		const firstFrame = Promise.all([
			context.queryClient
				.query({
					...friendsQueryOptions(userId),
					staleTime: "static",
				})
				.then((friends) =>
					preloadAvatars(
						friends.map((friend) => friend.avatar_urls),
						"md",
						AVATAR_PRELOAD_MS,
					),
				)
				.catch(() => undefined),
			context.queryClient
				.query({
					...thoughtsQueryOptions(userId),
					staleTime: "static",
				})
				.catch(() => undefined),
			context.queryClient
				.query({
					...characterLastMessageQueryOptions(user.character, userId),
					staleTime: "static",
				})
				.catch(() => undefined),
		])

		void context.queryClient
			.query(easyMatchStatusQueryOptions(userId))
			.catch(noop)
		void context.queryClient.query(subscriptionStatusQueryOptions()).catch(noop)
		void context.queryClient.query(userCardsQueryOptions(userId)).catch(noop)

		await firstFrame
	},
	component: HomePageWrapper,
})

function HomePageWrapper() {
	const { tab } = Route.useSearch()
	return <HomePage tab={tab} />
}
